import React, { useEffect, useMemo } from "react";
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from "react-i18next";

import Joyride, { STATUS } from 'react-joyride';

import itemsComponents from "../items";

import global from "../../config/global";
import storeActions from "../../store/actions";

const OnboardingModal = () => {
  const { t } = useTranslation();
  const { DocLink } = itemsComponents;

  const locale = useSelector((state) => state.system.locale);
  const isMobile = useSelector((state) => state.system.isMobile);
  const tutorialRunning = useSelector((state) => state.onboarding.tutorialRunning);
  const stepIndex = useSelector((state) => state.onboarding.stepIndex);
  const tutorialDetailRunning = useSelector((state) => state.onboarding.tutorialDetailRunning);
  const stepDetailIndex = useSelector((state) => state.onboarding.stepDetailIndex);

  const setStepIndex = (index) => {
    global.store.dispatch(storeActions.updateStepIndex(index));
  }

  const setStepDetailIndex = (index) => {
    global.store.dispatch(storeActions.updateStepDetailIndex(index));
  }

  const steps = useMemo(() => {
    return [
      {
        target: '.add-item',
        i18nKey: 'add_item',
        disableBeacon: true,
        placement: 'bottom',
        content: <div>
          <p className="text-2xl">
            {t('welcome_tour.steps.add_pw.detail.step1.title')}
          </p>
          <p className="text-black-500 mt-2 text-sm">
            <Trans
              i18nKey={'welcome_tour.steps.add_pw.detail.step1.text'}
              components={{
                b: <b className="font-semibold"></b>
              }}
            />
          </p>
        </div>,
      },
      {
        target: '.import-items',
        i18nKey: 'import_file',
        disableBeacon: true,
        placement: 'bottom',
        content: <div>
          <p className="text-2xl">
            {t('welcome_tour.steps.import_file.detail.step1.title')}
          </p>
          <p className="text-black-500 mt-2 text-sm">
            {t('welcome_tour.steps.import_file.detail.step1.text')}
          </p>
          <DocLink
            className="text-sm"
            title={t(`welcome_tour.steps.import_file.detail.step1.doc`)}
            docKey={'IMPORT_AND_EXPORT'}
          />
        </div>,
      },
      {
        target: '.sidebar-security-tool',
        i18nKey: 'browse_items',
        disableBeacon: true,
        placement: 'right',
        content: <div>
          <p className="text-2xl">
            {t('welcome_tour.steps.browse_items.detail.step1.title')}
          </p>
          <p className="text-black-500 mt-2 text-sm">
            <Trans
              i18nKey={'welcome_tour.steps.browse_items.detail.step1.text'}
              components={{
                b: <b className="font-semibold"></b>
              }}
            />
          </p>
        </div>,
      },
      {
        target: '.sidebar-settings',
        i18nKey: 'view_settings',
        disableBeacon: true,
        placement: 'right',
        content: <div>
          <p className="text-2xl">
            {t('welcome_tour.steps.view_settings.detail.step1.title')}
          </p>
          <p className="text-black-500 mt-2 text-sm">
            <Trans
              i18nKey={'welcome_tour.steps.view_settings.detail.step1.text'}
              components={{
                br: <br />,
                b: <b className="font-semibold"></b>
              }}
            />
          </p>
        </div>,
      },
      {
        target: '.user-info',
        i18nKey: 'view_profile',
        disableBeacon: true,
        placement: 'bottom',
        content: <div>
          <p className="text-2xl">
            {t('welcome_tour.steps.view_profile.detail.step1.title')}
          </p>
          <p className="text-black-500 mt-2 text-sm">
            <Trans
              i18nKey={'welcome_tour.steps.view_profile.detail.step1.text'}
              components={{
                br: <br />,
                b: <b className="font-semibold"></b>
              }}
            />
          </p>
        </div>,
      },
      {
        target: '.auto-fill',
      }
    ]
  }, [locale])

  const startTutorialRunning = (index) => {
    setStepIndex(index);
    setTimeout(() => {
      global.store.dispatch(storeActions.updateTutorialRunning(true));
    }, 100)
  }

  const stopTutorialRunning = () => {
    global.store.dispatch(storeActions.updateTutorialRunning(false));
    setStepIndex(0);
  }

  const stopTutorialDetailRunning = () => {
    global.store.dispatch(storeActions.updateTutorialDetailRunning(false));
    setStepDetailIndex(0);
  }

  const handleJoyrideCallback = (data) => {
    const { status, action, index, lifecycle } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      stopTutorialRunning();
    } else {
      if (action === 'next' && lifecycle == 'complete') {
        if ([0, 1].includes(index)) {
          stopTutorialRunning();
          if (index === 0) {
            global.navigate(global.keys.SETTINGS_IMPORT_EXPORT);
            startTutorialRunning(1)
          } else {
            if (isMobile) {
              global.store.dispatch(storeActions.updateRespCollapsed(false));
            }
            global.navigate(global.keys.VAULT);
            startTutorialRunning(2)
          }
        } else if (index < 4) {
          setStepIndex(index + 1);
        } else {
          stopTutorialRunning();
          global.store.dispatch(storeActions.updateGetExtensionVisible(true));
        }
      } else if (action === 'prev' && lifecycle == 'complete') {
        if ([1, 2, 4].includes(index)) {
          stopTutorialRunning();
          if (index === 1) {
            global.navigate(global.keys.VAULT);
            startTutorialRunning(0);
          } else if (index === 2) {
            global.navigate(global.keys.SETTINGS_IMPORT_EXPORT);
            startTutorialRunning(1);
          } else {
            if (isMobile) {
              global.store.dispatch(storeActions.updateRespCollapsed(false));
            }
            global.navigate(global.keys.VAULT);
            startTutorialRunning(3);
          }
        } else {
          setStepIndex(index - 1);
        }
      }
    }
  };

  const stepDetails = useMemo(() => {
    if (isMobile) {
      return []
    }
    if (stepIndex === 0) {
      return [
        {
          target: '.item-type-option-1',
          disableBeacon: true,
          placement: 'left',
          content: <div>
            <p className="text-2xl">
              {t('welcome_tour.steps.add_pw.detail.step2.title')}
            </p>
            <p className="text-black-500 mt-2 text-sm">
              <Trans
                i18nKey={'welcome_tour.steps.add_pw.detail.step2.text'}
                components={{
                  b: <b className="font-semibold"></b>
                }}
              />
            </p>
          </div>,
        },
        {
          target: '.vault-form-drawer',
          disableBeacon: true,
          placement: 'left',
          content: <div>
            <p className="text-2xl">
              {t('welcome_tour.steps.add_pw.detail.step3.title')}
            </p>
            <p className="text-black-500 mt-2 text-sm">
              <Trans
                i18nKey={'welcome_tour.steps.add_pw.detail.step3.text'}
                components={{
                  b: <b className="font-semibold"></b>
                }}
              />
            </p>
          </div>,
        },
        {
          target: '.next-detail',
        }
      ].filter((item) => !item.hide)
    }
    if (stepIndex === 1) {
      return [
        {
          target: '.import-format-options',
          disableBeacon: true,
          placement: 'left',
          content: <div>
            <p className="text-2xl">
              {t('welcome_tour.steps.import_file.detail.step2.title')}
            </p>
            <p className="text-black-500 mt-2 text-sm">
              <Trans
                i18nKey={'welcome_tour.steps.import_file.detail.step2.text'}
                components={{
                  b: <b className="font-semibold"></b>
                }}
              />
            </p>
          </div>,
        },
        {
          target: '.import-choose-file',
          disableBeacon: true,
          placement: 'left',
          content: <div>
            <p className="text-2xl">
              {t('welcome_tour.steps.import_file.detail.step3.title')}
            </p>
            <p className="text-black-500 mt-2 text-sm">
              <Trans
                i18nKey={'welcome_tour.steps.import_file.detail.step3.text'}
                components={{
                  b: <b className="font-semibold"></b>
                }}
              />
            </p>
          </div>,
        },
        {
          target: '.import-form-drawer',
          disableBeacon: true,
          placement: 'left',
          content: <div>
            <p className="text-2xl">
              {t('welcome_tour.steps.import_file.detail.step4.title')}
            </p>
            <p className="text-black-500 mt-2 text-sm">
              <Trans
                i18nKey={'welcome_tour.steps.import_file.detail.step4.text'}
                components={{
                  b: <b className="font-semibold"></b>
                }}
              />
            </p>
          </div>,
        },
        {
          target: '.next-detail',
        }
      ].filter((item) => !item.hide)
    }
    return []
  }, [stepIndex, locale, isMobile])

  const handleJoyrideDetailCallback = (data) => {
    const { status, action, index, lifecycle } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];
    if (finishedStatuses.includes(status)) {
      stopTutorialDetailRunning();
      if (stepIndex === 0) {
        global.navigate(global.keys.SETTINGS_IMPORT_EXPORT);
        startTutorialRunning(1);
      } else if (stepIndex === 1) {
        global.navigate(global.keys.VAULT);
        startTutorialRunning(2);
      }
    } else {
      if (stepIndex === 0) {
        if (action === 'next' && lifecycle == 'complete') {
          if (index < 1) {
            setStepDetailIndex(index + 1);
          } else {
            stopTutorialDetailRunning();
            global.navigate(global.keys.SETTINGS_IMPORT_EXPORT);
            startTutorialRunning(1);
          }
        }
      } else if (stepIndex === 1) {
        if (action === 'next' && lifecycle == 'complete') {
          if (index < 2) {
            setStepDetailIndex(index + 1);
          } else {
           stopTutorialDetailRunning();
            global.navigate(global.keys.VAULT);
            startTutorialRunning(2);
          }
        }
      }
    }
  }

  useEffect(() => {
    if (tutorialDetailRunning && !isMobile) {
      if (stepIndex === 0) {
        const targetElement = document.querySelector(stepDetails[stepDetailIndex].target);
        targetElement?.addEventListener('click', (e) => {
          if (stepDetailIndex === 0) {
            setStepDetailIndex(stepDetailIndex + 1);
          }
        })
      } else if (stepIndex === 1) {
        const targetElement = document.querySelector(stepDetails[stepDetailIndex].target);
        targetElement?.addEventListener('click', (e) => {
          if (stepDetailIndex < 2) {
            setStepDetailIndex(stepDetailIndex + 1);
          }
        })
      }
    }
  }, [
    isMobile,
    stepDetails,
    stepIndex,
    stepDetailIndex,
    tutorialDetailRunning
  ])

  return (
    <div className="onboarding-modal">
      <Joyride
        run={tutorialRunning}
        steps={steps}
        stepIndex={stepIndex}
        continuous
        showSkipButton
        spotlightClicks
        hideCloseButton
        disableOverlayClose
        scrollOffset={70}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#268334',
          },
          tooltipContent: {
            textAlign: 'left',
            padding: 0
          },
          tooltipFooter: {
          },
          spotlight: {
            backgroundColor: '#fff6',
            padding: 0,
          },
        }}
        locale={{
          back: t('button.back').toString(),
          next: t('button.next').toString(),
          close: t('button.close').toString(),
          skip: t('button.skip').toString(),
        }}
        callback={handleJoyrideCallback}
      />
      <Joyride
        run={tutorialDetailRunning}
        steps={stepDetails}
        stepIndex={stepDetailIndex}
        continuous
        showSkipButton
        spotlightClicks
        hideCloseButton
        disableOverlayClose
        hideBackButton
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#268334',
          },
          tooltipContent: {
            textAlign: 'left',
            padding: 0
          },
          tooltipFooter: {
          },
          spotlight: {
            backgroundColor: '#fff6',
            padding: 0,
          },
        }}
        locale={{
          back: t('button.back').toString(),
          next: t('button.next').toString(),
          close: t('button.close').toString(),
          skip: t('button.skip').toString(),
        }}
        callback={handleJoyrideDetailCallback}
      />
    </div>
  );
}

export default OnboardingModal;